import React from 'react';
import { Alert, Input, Typography } from 'antd';

const { Text } = Typography;

/**
 * WP-10 slice 3 — "make customer add information while approving".
 *
 * WP-02 already computes, server-side, exactly which customer fields are still
 * missing for approval, and `GET /api/public/estimates/:token` has been
 * returning that list as `completeness` all along. Nothing rendered it and
 * nothing sent the answers back, so the capture path was decided but
 * unreachable — the same shape of gap as `note` in slice 2.
 *
 * ── The list is the SERVER's, not this component's ────────────────────────
 *
 * There is no field list in this file. It renders whatever
 * `completeness.missing` contains, using the server's own `label`. That is
 * deliberate: a second copy of the rules here would be a second thing to keep in
 * step with `STAGE_RULES`, and the failure would be silent in the worst
 * direction — a customer prompted for something the API then refuses to accept,
 * or not prompted for something that blocks their approval.
 *
 * The server also pre-filters the list to fields a public token may actually
 * write (`PUBLIC_WRITABLE_FIELDS` — the postal address block only; contact
 * channels stay staff-entered, because a link-holder who could set `email`
 * would redirect every future notification for that customer). So this
 * component can render everything it is given without deciding anything.
 */

export interface MissingFieldSpec {
  entity: 'customer' | 'vehicle';
  field: string;
  /** Human-readable and safe to render directly — supplied by the server. */
  label: string;
}

/**
 * What the server sends in `completeness.fields` since 2026-07-29.
 *
 * A superset of `MissingFieldSpec`. The extra keys are what turn this from a
 * fill-the-blanks form into a check-your-details form:
 *
 * - `value`    the current stored value, so a wrong one can be SEEN and corrected
 * - `required` whether the gate refuses approval without it
 * - `writable` true = applied on approval; false = a PROPOSAL staff review first
 *
 * All three are optional so the older label-only shape still renders — the work
 * order payload and any cached client may still send it.
 */
export interface CaptureFieldSpec extends MissingFieldSpec {
  value?: string | null;
  required?: boolean;
  writable?: boolean;
}

export interface MissingInfoPromptProps {
  missing: CaptureFieldSpec[];
  values: Record<string, string>;
  onChange: (field: string, value: string) => void;
  /**
   * Per-field errors from a rejected submission, keyed by field name. WP-03
   * puts structure in sibling keys and keeps `error` a plain string; this is
   * the structure, mapped back onto the input that caused it.
   */
  fieldErrors?: Record<string, string>;
  /** Shown when the customer tried to approve with fields still blank. */
  blocked?: boolean;
}

export const MissingInfoPrompt: React.FC<MissingInfoPromptProps> = ({
  missing,
  values,
  onChange,
  fieldErrors = {},
  blocked = false,
}) => {
  if (!missing || missing.length === 0) return null;

  return (
    <div data-testid="missing-info-prompt" style={{ marginBottom: 16 }}>
      <Alert
        type={blocked ? 'error' : 'info'}
        showIcon
        message={
          blocked
            ? 'We still need these details before we can start the work'
            : 'Please check your details before you approve'
        }
        description={
          <div style={{ marginTop: 8 }}>
            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>
              Correct anything that&apos;s wrong and fill in what&apos;s missing. We use these to
              reach you, bill you, and find the vehicle — this is a mobile shop, so we come to
              you. Changes to your name, contact details or vehicle are checked by the shop
              before they&apos;re applied.
            </Text>
            {missing.map((m) => {
              const err = fieldErrors[m.field];
              // Prefer what the customer has typed; fall back to the stored value
              // so an existing entry is visible and can be CHECKED. `??` not `||`
              // so a deliberately-cleared field stays cleared.
              const shown = values[m.field] ?? m.value ?? '';
              const optional = m.required === false;
              return (
                <div key={m.field} style={{ marginBottom: 10 }}>
                  <Text style={{ fontSize: 13, display: 'block', marginBottom: 2 }}>
                    {m.label}
                    {optional && (
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        {' '}
                        (optional — helps us prepare)
                      </Text>
                    )}
                  </Text>
                  <Input
                    data-testid={`missing-field-${m.field}`}
                    aria-label={m.label}
                    status={err ? 'error' : undefined}
                    value={shown}
                    onChange={(e) => onChange(m.field, e.target.value)}
                    // 16px keeps mobile Safari from zooming the viewport on
                    // focus; this prompt sits directly above the approve button.
                    style={{ fontSize: 16 }}
                  />
                  {err && (
                    <Text type="danger" style={{ fontSize: 12 }} data-testid={`missing-error-${m.field}`}>
                      {err}
                    </Text>
                  )}
                </div>
              );
            })}
          </div>
        }
      />
    </div>
  );
};

export default MissingInfoPrompt;
